import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, Check, RefreshCw, Send, Reply, Clock, Repeat, User, X } from "lucide-react";
import { subscribeSenderPick, clearRememberedSender, type SenderPickRequest, type SenderPickAccount, type SenderPickAction } from "@/lib/senderPick";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";


/**
 * Singleton modal host. Opens whenever a chat tool calls `requestSenderPick()`
 * with multiple connected email accounts. The user picks which account to send
 * from, then sees a confirmation step so they can still **change account**
 * before the email actually leaves. The promise resolves with the chosen
 * account or `null` on cancel.
 */
export default function SenderPickHost() {
  const isMobile = useIsMobile();
  const [req, setReq] = useState<SenderPickRequest | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  // "select" → list of accounts, "confirm" → review screen with "change account"
  const [step, setStep] = useState<"select" | "confirm">("select");


  useEffect(() => subscribeSenderPick((r) => {
    setReq(r);
    setStep("select");
    if (r) {
      const def = r.accounts.find((a) => a.isDefault) ?? r.accounts[0];
      setPicked(def ? `${def.provider}:${def.email}` : null);
    }
  }), []);

  const close = (account: SenderPickAccount | null) => {
    req?.resolve(account);
  };

  if (!req) return null;

  const selected = req.accounts.find((a) => `${a.provider}:${a.email}` === picked) ?? null;

  const renderAccountBadge = (provider: SenderPickAccount["provider"]) => (
    <span
      className={cn(
        "text-xs uppercase font-semibold px-1.5 py-0.5 rounded-sm shrink-0 bg-muted text-muted-foreground",
        provider === "gmail" && "bg-primary/10 text-primary",
        provider === "outlook" && "bg-secondary text-secondary-foreground",
      )}
    >
      {provider === "gmail" ? "Gmail" : provider === "outlook" ? "Outlook" : "Thiia"}
    </span>
  );

  const ACTION_META: Record<SenderPickAction, { icon: typeof Mail; label: string; verb: string }> = {
    send: { icon: Send, label: "Envoi d'email", verb: "Envoyer" },
    reply: { icon: Reply, label: "Réponse à un email", verb: "Répondre" },
    schedule: { icon: Clock, label: "Email planifié", verb: "Planifier" },
    sequence: { icon: Repeat, label: "Séquence d'emails", verb: "Lancer la séquence" },
  };
  const action: SenderPickAction = req.action ?? "send";
  const ActionIcon = ACTION_META[action].icon;
  const actionLabel = ACTION_META[action].label;
  const actionVerb = ACTION_META[action].verb;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) close(null); }}>
      <DialogContent
        className="w-[calc(100vw-1rem)] max-w-md p-3 sm:p-6 rounded-lg overflow-y-auto flex flex-col gap-3 sm:gap-4"
        style={{
          // `dvh` follows Safari's address-bar collapse/expand on iOS so the
          // modal never gets clipped behind the dynamic UI chrome. Fallback to
          // `vh` for older browsers via `min()`.
          maxHeight: "min(92dvh, 92vh)",
          // Respect iOS home-indicator / Android gesture bar so the Footer
          // stays tappable in landscape too.
          paddingBottom: "max(env(safe-area-inset-bottom, 0px), 0.75rem)",
          paddingTop: "max(env(safe-area-inset-top, 0px), 0.75rem)",
        }}
      >
        <DialogHeader className="text-left">
          {/* Animated step indicator — re-keys on `step` so the dot/label
              swap plays the `fade-in` keyframe each time, giving an
              unambiguous "you've moved to the next step" cue. */}
          <div
            key={`stepper-${step}`}
            className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold mb-1 animate-fade-in"
            aria-live="polite"
          >
            <span className={cn(
              "h-1.5 rounded-full transition-all duration-300",
              step === "select" ? "w-6 bg-primary" : "w-3 bg-primary/40",
            )} />
            <span className={cn(
              "h-1.5 rounded-full transition-all duration-300",
              step === "confirm" ? "w-6 bg-primary" : "w-3 bg-primary/40",
            )} />
            <span className="ml-1.5 text-muted-foreground">
              {step === "select" ? "Étape 1/2 — Sélection" : "Étape 2/2 — Confirmation"}
            </span>
          </div>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <ActionIcon className="w-4 h-4 text-primary shrink-0" />
            <span key={`title-${step}`} className="truncate animate-fade-in">
              {step === "select" ? "Choisir le compte" : `Confirmer : ${actionVerb.toLowerCase()}`}
            </span>
          </DialogTitle>
          <DialogDescription key={`desc-${step}`} className="text-xs sm:text-sm animate-fade-in">
            {step === "select"
              ? "Plusieurs comptes email sont connectés. Sélectionnez celui que Thiia doit utiliser."
              : "Vérifiez l'expéditeur, puis validez pour lancer l'envoi."}
          </DialogDescription>
        </DialogHeader>

        {/* Action context card — always visible so the user knows exactly
            what's about to happen and to whom. Every text node uses
            `min-w-0` + a wrapping/truncating strategy so a long subject,
            email or context never pushes the modal beyond the viewport. */}
        <div className="rounded-md border border-border/60 bg-muted/40 px-3 py-2.5 space-y-1.5 min-w-0 max-w-full overflow-hidden">
          <div className="flex items-center gap-2 text-[13px] uppercase tracking-wide text-muted-foreground font-semibold min-w-0">
            <ActionIcon className="w-3 h-3 shrink-0" />
            <span className="truncate">{actionLabel}</span>
            {req.scheduledAt && (
              <span className="ml-auto normal-case text-muted-foreground/80 font-normal truncate max-w-[50%]">
                {req.scheduledAt}
              </span>
            )}
          </div>
          {req.recipient && (
            <div className="flex items-center gap-1.5 text-sm font-medium text-foreground min-w-0">
              <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              {/* Email addresses can be 60+ chars without spaces — `break-all`
                  guarantees they wrap inside the bubble even if truncate fails
                  (e.g. when the parent grows to fit). `line-clamp-1` keeps
                  the visual height predictable on the happy path. */}
              <span
                className="min-w-0 flex-1 truncate break-all"
                title={req.recipient}
              >
                {req.recipient}
              </span>
            </div>
          )}
          {req.subject && (
            <div className="flex items-start gap-1.5 text-xs text-muted-foreground min-w-0">
              <span className="font-medium shrink-0 not-italic">Objet :</span>
              <p
                className="italic break-words line-clamp-2 min-w-0 flex-1 leading-snug"
                style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                title={req.subject}
              >
                {req.subject}
              </p>
            </div>
          )}
          {!req.recipient && !req.subject && req.context && (
            <p
              className="text-xs text-muted-foreground break-words line-clamp-3 leading-snug"
              style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
              title={req.context}
            >
              {req.context}
            </p>
          )}
        </div>


        {step === "select" ? (
          <div
            key="step-select"
            className="flex flex-col gap-2 py-1 overflow-y-auto -mx-1 px-1 overscroll-contain animate-fade-in"
            style={{
              maxHeight: "min(50dvh, 50vh)",
              // iOS momentum scroll + hide scrollbar visual jank during fling
              WebkitOverflowScrolling: "touch",
              scrollbarGutter: "stable",
            }}
            role="listbox"
            aria-label="Comptes email disponibles"
          >
            {req.accounts.map((a) => {
              const value = `${a.provider}:${a.email}`;
              const isPicked = picked === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="option"
                  aria-selected={isPicked}
                  onClick={() => {
                    setPicked(value);
                    if (isMobile) setStep("confirm");
                  }}
                  className={cn(
                    // Larger touch target on mobile (≥48px per WCAG 2.5.5),
                    // crisp focus ring that survives keyboard + touch.
                    "w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-3 text-left transition-all min-h-[48px] sm:min-h-[44px]",
                    "active:scale-[0.985] active:bg-accent/60",
                    "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                    isPicked
                      ? "border-primary bg-primary/10 ring-2 ring-primary/40 shadow-xs"
                      : "border-border/60 hover:border-border hover:bg-accent/50",
                  )}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {renderAccountBadge(a.provider)}
                    <div className="min-w-0 flex-1">
                      <span className={cn(
                        "block text-sm truncate",
                        isPicked ? "font-semibold text-foreground" : "font-medium",
                      )}>
                        {a.email}
                      </span>
                      {a.isDefault && (
                        <span className="block sm:hidden text-[10px] text-muted-foreground">
                          Compte par défaut
                        </span>
                      )}
                    </div>
                    {a.isDefault && (
                      <span className="hidden sm:inline text-[13px] text-muted-foreground shrink-0">(défaut)</span>
                    )}
                  </div>
                  {isPicked ? (
                    <Check className="w-5 h-5 text-primary shrink-0" />
                  ) : isMobile ? (
                    <Send className="w-4 h-4 text-muted-foreground/70 shrink-0" />
                  ) : null}
                </button>
              );
            })}
          </div>
        ) : (
          <div key="step-confirm" className="py-2 space-y-2 animate-scale-in">
            <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-3 flex items-center gap-2.5 shadow-xs">
              {selected && renderAccountBadge(selected.provider)}
              <div className="min-w-0 flex-1">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                  Expéditeur
                </p>
                <p className="text-xs sm:text-sm font-medium truncate">{selected?.email}</p>
              </div>
              <Check className="w-4 h-4 text-primary shrink-0" />
            </div>
            {/* Memory hint — informs the coach that this choice will be reused
                automatically on next emails, with a one-click escape hatch. */}
            <div className="flex items-center justify-between gap-2 px-1 text-[13px] text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Check className="w-3 h-3 text-primary/70" />
                Ce compte sera réutilisé automatiquement la prochaine fois.
              </span>
              <button
                type="button"
                onClick={() => clearRememberedSender(req.coachId)}
                className="inline-flex items-center gap-1 underline decoration-dotted hover:text-foreground transition-colors shrink-0"
                title="Oublier ce compte et redemander la prochaine fois"
              >
                <X className="w-3 h-3" />
                Oublier
              </button>
            </div>
          </div>
        )}

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-2">
          {step === "select" ? (
            <>
              <Button
                variant="outline"
                onClick={() => close(null)}
                className="w-full sm:w-auto h-10 sm:h-9"
              >
                Annuler
              </Button>
              <Button
                onClick={() => setStep("confirm")}
                disabled={!selected}
                className="gap-1.5 w-full sm:w-auto h-10 sm:h-9 hidden sm:inline-flex"
              >
                <Mail className="w-3.5 h-3.5" />
                Continuer
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setStep("select")}
                className="gap-1.5 w-full sm:w-auto h-10 sm:h-9"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Changer de compte
              </Button>
              <Button
                onClick={() => close(selected)}
                disabled={!selected}
                className="gap-1.5 w-full sm:w-auto h-10 sm:h-9"
              >
                <ActionIcon className="w-3.5 h-3.5" />
                {actionVerb}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

